<template>
  <nav
    class="flex flex-col mt-2 gap-4 md:gap-0 xl:flex-row xl:justify-between xl:items-center xl:w-full xl:gap-4"
  >
    <div class="relative xl:shrink-[20]">
      <div class="flex gap-4 justify-center items-center mb-4 md:items-start xl:justify-start">
        <div class="relative w-20 aspect-square md:w-16 md:h-16">
          <!--SHOW IMAGE-->
          <div
            id="show-image-loader"
            v-if="!initialized || noPerformancesByDate"
            class="w-full h-full rounded-full object-cover border-2 border-linear-strong shadow-lg bg-surface-base"
          />
          <img
            id="show-image"
            v-else-if="selectedPerformanceShow"
            :src="selectedPerformanceShow.srcset.sqSm.src"
            :alt="selectedPerformanceShow.name"
            class="w-full h-full rounded-full object-cover border-2 border-linear-strong shadow-lg"
          />
        </div>

        <div class="relative flex flex-col gap-1 md:justify-start md:items-start md:shrink-[20]">
          <div
            class="flex flex-col items-center gap-3 md:flex-row md:gap-6 md:pt-3 md:justify-center md:w-full xl:justify-start"
          >
            <!--DATE NAVIGATOR-->
            <PerformanceDateNavigator
              :modelValue="dateFilter"
              @update:modelValue="emit('update:dateFilter', $event)"
              @update:performance="emit('update:performance', $event)"
            />

            <div class="flex flex-row gap-2 xs:gap-4 items-center justify-center sm:justify-start">
              <!--PERFORMANCE TIMES-->
              <router-link
                :id="`perf-time-link-${link.id}`"
                v-for="link in performancesByDate"
                :key="link.id"
                :to="{ params: { performanceId: link.id } }"
                :class="
                  route.params.performanceId === link.id.toString()
                    ? 'text-fg-primary'
                    : 'text-fg-link-primary'
                "
                class="perf-time-link font-heading font-semibold text-xl"
                replace
              >
                {{ settingStore.timeStr(link.datetime) }}
              </router-link>
            </div>
          </div>
          <!--SHOW NAME (DESKTOP SCREEN)-->
          <p
            id="show-name-label"
            class="text-sm text-fg-secondary text-center hidden md:block xl:text-left pl-5"
          >
            {{ showNameLabel }}
          </p>
        </div>
      </div>

      <!--SHOW NAME (MOBILE SCREEN)-->
      <p
        id="show-name-label"
        class="text-sm text-fg-secondary truncate max-w-full text-center block md:hidden"
      >
        {{ showNameLabel }}
      </p>
    </div>

    <!--PERFORMANCE PAGE LINKS-->
    <PerformancePageLinks class="xl:grow-[20]" v-if="canShowPageLinks" />
  </nav>
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia'
import { computed, toRefs } from 'vue'
import { useRoute } from 'vue-router'

import { StorePerformance, StoreShow } from '@/models'
import PerformancePageLinks from '@/modules/performance/components/molecules/PerformancePageLinks.vue'
import PerformanceDateNavigator from '@/modules/performance/components/organisms/PerformanceDateNavigator.vue'
import { usePerformanceReservationStore } from '@/modules/performance/stores/PerformanceReservationStore'
import { usePerformanceStore } from '@/modules/performance/stores/PerformanceStore'
import { useSettingStore } from '@/stores/setting'

import { PerformanceId, ShowRead } from '@generated/types'

const props = defineProps<{
  dateFilter: string
  performancesByDate: StorePerformance[]
}>()
const { performancesByDate } = toRefs(props)

const emit = defineEmits<{
  // eslint-disable-next-line no-unused-vars
  (e: 'update:dateFilter', date: string): void
  // eslint-disable-next-line no-unused-vars
  (e: 'update:performance', performanceId: PerformanceId): void
}>()

const route = useRoute()

const settingStore = useSettingStore()
const performanceStore = usePerformanceStore()
const performanceReservationStore = usePerformanceReservationStore()
const { selectedPerformanceId, selectedPerformance, isLoadingSelectedPerformance, initialized } =
  storeToRefs(performanceReservationStore)
const selectedPerformanceShow = computed<ShowRead | StoreShow | undefined>(() => {
  if (!selectedPerformanceId.value) return undefined
  if (selectedPerformance.value) return selectedPerformance.value.show
  return performanceStore.performanceById(selectedPerformanceId.value).show
})
const noPerformancesByDate = computed<boolean>(() => performancesByDate.value.length === 0)
const showNameLabel = computed<string>(() => {
  if (!initialized.value) return 'Loading...'
  if (noPerformancesByDate.value) return 'No shows for this date'
  if (selectedPerformanceShow.value) return selectedPerformanceShow.value.name
  return ''
})
const canShowPageLinks = computed<boolean>(() => {
  return !noPerformancesByDate.value && !isLoadingSelectedPerformance.value && initialized.value
})
</script>
